import type { WebContainer } from '@webcontainer/api';
import { workbenchStore } from '../../stores/workbench';
import { getHeadlessTemplateFiles } from '../../templates/headlessTemplate';
import { HOT_EDIT_BRIDGE_REL_PATHS } from '../../utils/mergedFiles';

function normalizePath(filePath: string): string {
  return filePath.replace(/\\/g, '/').replace(/^\/+/, '');
}

function buildPreserveSet(keep: string[]): Set<string> {
  const preserve = new Set<string>();

  for (const [path] of Object.entries(getHeadlessTemplateFiles())) {
    const normalized = normalizePath(path);
    if (normalized.startsWith('src/')) {
      preserve.add(normalized);
    }
  }

  for (const path of HOT_EDIT_BRIDGE_REL_PATHS) {
    preserve.add(normalizePath(path));
  }

  for (const path of keep) {
    const normalized = normalizePath(path);
    if (normalized.startsWith('src/')) {
      preserve.add(normalized);
    }
  }

  for (const [path, file] of Object.entries(workbenchStore.files.get())) {
    if (file?.modified) {
      const normalized = normalizePath(path);
      if (normalized.startsWith('src/')) {
        preserve.add(normalized);
      }
    }
  }

  return preserve;
}

async function listFilesRecursive(wc: WebContainer, dir: string): Promise<string[]> {
  const entries = await wc.fs.readdir(dir, { withFileTypes: true });
  const files: string[] = [];

  for (const entry of entries) {
    const fullPath = dir === '.' ? entry.name : `${dir}/${entry.name}`;
    if (entry.isDirectory()) {
      files.push(...(await listFilesRecursive(wc, fullPath)));
    } else if (entry.isFile()) {
      files.push(fullPath);
    }
  }

  return files;
}

/**
 * Remove generated files under src/ that are not in the preserve set.
 * Template infrastructure, seeded paths, and user-modified files are kept.
 */
export async function executePrune(wc: WebContainer | null, keep: string[]): Promise<string> {
  const preserve = buildPreserveSet(keep);
  let srcFiles: string[];

  if (wc) {
    try {
      srcFiles = await listFilesRecursive(wc, 'src');
    } catch {
      return 'Prune skipped: src/ not found';
    }
  } else {
    // No filesystem to walk in the light preview. The store holds every generated file, and
    // the template's own files are in the preserve set anyway.
    srcFiles = Object.keys(workbenchStore.files.get()).filter((path) => path.startsWith('src/'));
  }

  const toRemove = srcFiles.map(normalizePath).filter((path) => !preserve.has(path));

  for (const path of toRemove) {
    try {
      await wc?.fs.rm(path);
      workbenchStore.removeFile(path);
    } catch {
      // Best-effort — stale paths should not block generation.
    }
  }

  const reset = await resetTemplateCopies(wc, keep);

  return toRemove.length + reset > 0
    ? `Pruned ${toRemove.length} stale file(s) under src/, reset ${reset} to the template`
    : 'No stale files to prune under src/';
}

/** Written into the phone app shell's `index.html` by Chef (`mobileShell.ts`), and nowhere else. */
const MOBILE_INDEX_HTML_MARKER = 'IDEOZ_PROTOTYPE_TEMPLATE';

/**
 * Hands the template's own paths back to the template where the workspace holds another
 * version of them that nothing asked to keep.
 *
 * The preserve set keeps every path the template has, so a phone app's `src/App.tsx` (which
 * imports Framework7) outlived a cold turn that built a web app in the same tab, and so did its
 * `index.html`, which is outside `src/` and was never pruned. A Framework7 import reads as a phone
 * app (`templateIdForFiles`), so the web app was previewed with the mobile template and crashed,
 * and the phone page stayed in the project until a refresh (TeamPulse, then TaskFlow Hub,
 * 1 Oct 2026). Kept paths, founder edits and the hot-edit bridge are left alone. Returns how many
 * paths were reset.
 */
async function resetTemplateCopies(wc: WebContainer | null, keep: string[]): Promise<number> {
  const held = new Set(keep.map(normalizePath));
  for (const path of HOT_EDIT_BRIDGE_REL_PATHS) held.add(normalizePath(path));
  const template = getHeadlessTemplateFiles();
  let count = 0;
  for (const [rawPath, file] of Object.entries(workbenchStore.files.get())) {
    const path = normalizePath(rawPath);
    if (!file || file.modified || held.has(path)) continue;
    const original = template[path];
    const stale =
      path === 'index.html'
        ? typeof file.content === 'string' && file.content.includes(MOBILE_INDEX_HTML_MARKER)
        : path.startsWith('src/') && original !== undefined && file.content !== original;
    if (!stale) continue;
    try {
      // The WebContainer serves files from disk, so it gets the template's copy back; the light
      // preview reads the template whenever the store has no copy of its own.
      if (wc && original !== undefined) await wc.fs.writeFile(path, original);
      workbenchStore.removeFile(rawPath);
      count++;
    } catch {
      // Best-effort, like the prune above.
    }
  }
  return count;
}
